import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

/**
 * Auth state lives in two places:
 *   1. An httpOnly `access_token` cookie set by the backend on login/register.
 *      This is the source of truth across page reloads and is invisible to
 *      JavaScript (mitigates XSS token theft).
 *   2. The same token kept in React state for *this* tab session — used to
 *      send an `Authorization: Bearer` header so we keep working even on
 *      cross-origin or cookie-less environments.
 *
 * On mount we always probe `/api/auth/me` with credentials so the cookie
 * alone is enough to restore the session.
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchMe = useCallback(async (bearer) => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
        withCredentials: true,
      });
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  // On mount probe the session — the httpOnly cookie (if any) is enough.
  useEffect(() => {
    (async () => {
      await fetchMe();
      setLoading(false);
    })();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, {
        headers: authHeader(),
        withCredentials: true,
      });
    } catch (err) {
      console.error("logout failed:", err);
    }
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      register,
      logout,
      authHeader,
      API,
      setToken,
      refreshUser: () => fetchMe(token),
    }),
    [user, token, loading, authHeader, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
